import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useSite } from '../contexts/SiteContext';
import { useEditableContent } from '../hooks/useEditableContent';
import SafeIcon from '../common/SafeIcon';
import EditableContent from '../components/EditableContent';
import ShareButton from '../components/ShareButton';
import { clearFormSource, getFormTracking, submitAoxForm } from '../lib/aoxForms';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiPhone, FiMail, FiSend, FiCheckCircle, FiAlertCircle, FiShield } = FiIcons;

const ContactPageSecure = () => {
  const { siteConfig } = useSite();
  const { rows: editableRows } = useEditableContent('contact');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: 'other', message: '' });
  const [formStatus, setFormStatus] = useState({ submitted: false, success: false, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getContent = (section, key, fallback) => {
    const item = (editableRows || []).find((row) => row.section === section && row.content_key === key);
    return item?.content || fallback;
  };

  const successMessage = getContent('form', 'success', 'Το μήνυμά σας στάλθηκε επιτυχώς! Θα επικοινωνήσουμε μαζί σας σύντομα.');
  const errorMessage = getContent('form', 'error', 'Προέκυψε σφάλμα κατά την αποστολή. Παρακαλώ δοκιμάστε ξανά.');

  const contactInfo = useMemo(() => [
    { icon: FiMapPin, title: 'Διεύθυνση', value: siteConfig.address || 'Χανιά' },
    { icon: FiPhone, title: 'Τηλέφωνο', value: siteConfig.phone || '' },
    ...(siteConfig.email && siteConfig.email !== '_' ? [{ icon: FiMail, title: 'Email', value: siteConfig.email }] : []),
  ], [siteConfig.address, siteConfig.phone, siteConfig.email]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ submitted: false, success: false, message: '' });

    try {
      if (!formData.name || !formData.email || !formData.phone || !formData.message) {
        throw new Error('Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία.');
      }

      await submitAoxForm({
        formType: 'contact',
        payload: { ...formData, ...getFormTracking('contact_page_form', 'contact') },
        siteConfig,
        action: 'aox_contact',
      });

      clearFormSource('contact');
      setFormStatus({ submitted: true, success: true, message: successMessage });
      setFormData({ name: '', email: '', phone: '', subject: 'other', message: '' });
    } catch (error) {
      setFormStatus({ submitted: true, success: false, message: error?.message || errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="relative bg-primary-700 py-16 text-white">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl font-black md:text-5xl">
            <EditableContent page="contact" section="header" contentKey="title" defaultValue="Επικοινωνήστε Μαζί μας" tag="span" />
          </motion.h1>
          <p className="mt-4 text-xl text-primary-100">
            <EditableContent page="contact" section="header" contentKey="subtitle" defaultValue="Είμαστε πάντα στη διάθεσή σας για οποιαδήποτε πληροφορία" tag="span" />
          </p>
        </div>
        <div className="absolute right-4 top-4"><ShareButton title="Επικοινωνία - Α.Ο. Χανιά" compact variant="outline" /></div>
      </section>

      <section className="bg-white py-12">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 md:grid-cols-3">
          {contactInfo.map((item) => (
            <div key={item.title} className="rounded-2xl bg-gray-50 p-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-100"><SafeIcon icon={item.icon} className="h-6 w-6 text-primary-600" /></div>
              <h3 className="font-black text-gray-900">{item.title}</h3>
              <p className="mt-2 text-gray-600">{item.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-3xl px-4">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-black text-gray-900">
              <EditableContent page="contact" section="form" contentKey="title" defaultValue="Φόρμα Επικοινωνίας" tag="span" />
            </h2>
            <p className="mt-3 text-gray-600">
              <EditableContent page="contact" section="form" contentKey="subtitle" defaultValue={siteConfig.contact_text || 'Συμπληρώστε τη φόρμα και θα επικοινωνήσουμε μαζί σας σύντομα.'} tag="span" />
            </p>
          </div>

          <div className="rounded-2xl bg-white p-8 shadow-xl">
            {formStatus.submitted ? (
              <div className={`rounded-xl p-6 text-center ${formStatus.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                <SafeIcon icon={formStatus.success ? FiCheckCircle : FiAlertCircle} className="mx-auto mb-4 h-12 w-12" />
                <p className="font-semibold">{formStatus.message}</p>
                {!formStatus.success && <button type="button" onClick={() => setFormStatus({ submitted: false, success: false, message: '' })} className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-white">Προσπαθήστε ξανά</button>}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid gap-5 md:grid-cols-2">
                  <label className="block"><span className="mb-1 block text-sm font-bold">Ονοματεπώνυμο *</span><input className="w-full rounded-lg border px-3 py-2.5" name="name" value={formData.name} onChange={handleChange} required maxLength={160} /></label>
                  <label className="block"><span className="mb-1 block text-sm font-bold">Email *</span><input className="w-full rounded-lg border px-3 py-2.5" type="email" name="email" value={formData.email} onChange={handleChange} required maxLength={254} /></label>
                  <label className="block"><span className="mb-1 block text-sm font-bold">Τηλέφωνο *</span><input className="w-full rounded-lg border px-3 py-2.5" type="tel" name="phone" value={formData.phone} onChange={handleChange} required maxLength={60} /></label>
                  <label className="block"><span className="mb-1 block text-sm font-bold">Θέμα</span><select className="w-full rounded-lg border px-3 py-2.5" name="subject" value={formData.subject} onChange={handleChange}><option value="academy">Εγγραφή στην Ακαδημία</option><option value="team">Πληροφορίες για την ομάδα</option><option value="sponsor">Χορηγίες</option><option value="media">Media & Δημοσιότητα</option><option value="other">Άλλο</option></select></label>
                </div>
                <label className="block"><span className="mb-1 block text-sm font-bold">Μήνυμα *</span><textarea className="w-full rounded-lg border px-3 py-2.5" name="message" rows="6" value={formData.message} onChange={handleChange} required maxLength={5000} /></label>
                {siteConfig.recaptcha_enabled && <p className="flex items-center justify-center gap-2 text-xs text-gray-500"><SafeIcon icon={FiShield} className="h-4 w-4" />Η φόρμα προστατεύεται με reCAPTCHA v3.</p>}
                <button disabled={isSubmitting} className="flex w-full items-center justify-center rounded-xl bg-primary-600 px-6 py-3 font-black text-white disabled:opacity-60"><SafeIcon icon={FiSend} className="mr-2 h-5 w-5" />{isSubmitting ? 'Αποστολή...' : 'Αποστολή'}</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPageSecure;
